import type { CompletionConsideration } from '@year13/kui/composables/useCompletionSteps';

import type { ConsiderationAreaColor } from '~/graphql/generated/graphql';

import { toConsiderationColor } from '~/utils/considerationAreaColor';

interface ProfileConsiderationArea {
  id: string;
  label: string;
  color?: ConsiderationAreaColor;
  iconFile?: { publicUrl?: string | null } | null;
  userLevel?: number | null;
  userProgressPercentage?: number | null;
}

interface BuildArgs {
  assetConsiderationAreaIds: string[];
  before: ProfileConsiderationArea[];
  after: ProfileConsiderationArea[];
}

/**
 * Build the before→after completion deltas for the CAs a just-completed LA belongs to.
 * Both states are read straight from profile snapshots — `before` captured before the
 * completion mutation, `after` refetched once it has committed — so the real level/progress
 * is used directly, with no stepping or config-derived guesswork.
 * Iterates `after` (stable order) and keeps only CAs the asset maps to; a CA absent from the
 * `before` snapshot (first ever completion) defaults to level 0, 0%.
 */
export const buildCompletionConsiderations = ({
  assetConsiderationAreaIds,
  before,
  after,
}: BuildArgs): CompletionConsideration[] => {
  const wanted = new Set(assetConsiderationAreaIds);
  const beforeById = new Map(before.map(area => [area.id, area]));

  return after
    .filter(area => wanted.has(area.id))
    .map((area) => {
      const beforeArea = beforeById.get(area.id);

      return {
        areaId: area.id,
        areaLabel: area.label,
        color: area.color ? toConsiderationColor(area.color) : undefined,
        illustration: area.iconFile?.publicUrl ?? undefined,
        beforeLevel: beforeArea?.userLevel ?? 0,
        beforeProgress: beforeArea?.userProgressPercentage ?? 0,
        afterLevel: area.userLevel ?? 0,
        afterProgress: area.userProgressPercentage ?? 0,
      };
    });
};
